import { observer } from 'mobx-react';
import * as React from 'react';

import { Experiment, ExperimentControl, ExperimentVariant } from '@shared-ui/experiment-context';
import { useClickTracker } from '@shared-ui/clickstream-analytics-context';
import { ViewLarge, Viewport, ViewSmall } from '@shared-ui/viewport-context';

import { SearchCriteriaModel } from 'src/common/stores/models';

import { FilterModel, FilterOptionModel } from 'components/common/search-form/fields/filter-types';
import { SortAndFilterModel } from './filter-types';

import { UitkExpandoPeek } from 'uitk-react-expando';
import { UitkHeading } from 'uitk-react-text';
import { UitkLayoutGrid, UitkLayoutGridColumnRow, UitkLayoutGridItem } from 'uitk-react-layout-grid';
import { UitkSpacing } from 'uitk-react-spacing';

import { getFilterTypesWithOption, getValueArray, updateFilterTypeValue } from './field-utils';

import { CheckboxInput } from 'components/common/search-form/fields/checkbox-input';
import { FormFieldBinding } from 'components/common/search-form/fields/form-field-binding';

const CheckboxGroupComponent = ({
  getFilterType,
  isChecked,
  model,
  modelKey,
  sortAndFilterOptions,
  updateModel,
  handleChange,
  keepExpanded,
}: {
  getFilterType?: (option: FilterOptionModel) => string;
  isChecked?: (option: FilterOptionModel) => boolean;
  model: SearchCriteriaModel;
  modelKey: string;
  sortAndFilterOptions: SortAndFilterModel;
  updateModel: any;
  handleChange?: (evt) => void;
  keepExpanded?: boolean;
}) => {
  const track = useClickTracker();

  const checkboxChange = evt => {
    const target = evt.target;
    const { value } = target;

    const checkbox: FilterModel = sortAndFilterOptions && sortAndFilterOptions[modelKey];
    const checkboxName: string = checkbox && checkbox.title;
    const modelValue = model && model[checkboxName];

    if (typeof updateModel !== 'function' || modelValue === undefined) {
      return;
    }

    // Get all filterTypes that have this option
    const filterTypesToUpdate = getFilterTypesWithOption(sortAndFilterOptions, value);

    // Update the option in each filterType
    filterTypesToUpdate.forEach(key => {
      const filterTypeModel: FilterModel = sortAndFilterOptions[key];
      const currentValueArray = getValueArray(model[filterTypeModel.title]);

      let newArrayValue = updateFilterTypeValue(currentValueArray, value);

      // Update first/last value when isRange true e.g. price */
      if (filterTypeModel.range && newArrayValue.length > 0) {
        const { firstIndex, lastIndex } = getFirstAndLastIndices(filterTypeModel.options, newArrayValue);
        newArrayValue = [filterTypeModel.options[firstIndex].value, filterTypeModel.options[lastIndex].value];
      }

      updateModel({
        target: {
          name: filterTypeModel.title,
          value: newArrayValue,
        },
      });
    });
  };

  const getFirstAndLastIndices = (checkboxOptions: FilterOptionModel[], modelValues: string[] | string) => {
    let firstIndex: number;
    let lastIndex: number;

    checkboxOptions.forEach((option, i) => {
      const optionChecked = modelValues.includes(option.value);

      if (firstIndex === undefined && optionChecked) {
        firstIndex = i;
      }
      if (optionChecked) {
        lastIndex = i;
      }
    });

    return { firstIndex, lastIndex };
  };

  const checkbox = sortAndFilterOptions && sortAndFilterOptions[modelKey];
  const checkboxOptions = checkbox && checkbox.options;
  const showListState = checkbox?.showListOptions;

  const getInitialViewExpandedState = () =>
    keepExpanded &&
    showListState?.hiddenThreshold &&
    checkboxOptions.slice(showListState.hiddenThreshold).some(checkboxOption => checkboxOption.isSelected);

  const [viewExpanded, setViewExpanded] = React.useState(getInitialViewExpandedState());

  if (!checkboxOptions || checkboxOptions.length === 0) {
    return null;
  }

  const checkboxes = (
    <Checkboxes
      getFilterType={getFilterType}
      getFirstAndLastIndices={getFirstAndLastIndices}
      handleChange={handleChange || checkboxChange}
      isChecked={isChecked}
      model={model}
      modelKey={modelKey}
      sortAndFilterOptions={sortAndFilterOptions}
      children={Array.from({ length: checkboxOptions.length }, (v, i) => i)}
    />
  );

  return (
    <>
      <UitkSpacing margin={{ blockstart: 'six' }}>
        <fieldset>
          <UitkSpacing margin={{ blockend: 'two' }}>
            <legend className="filter-title">
              <UitkHeading size={7} tag="h4">
                {checkbox.label}
              </UitkHeading>
            </legend>
          </UitkSpacing>
          {showListState.hiddenThreshold && checkboxOptions.length > showListState.hiddenThreshold ? (
            <>
              <span data-stid="checkbox-group-expando-peek"></span>
              <UitkExpandoPeek
                items={showListState.hiddenThreshold}
                handleClick={() => setViewExpanded(!viewExpanded)}
                isVisible={viewExpanded}
                analytics={{
                  id: checkbox.title,
                  callback: id => {
                    const idArr = id.split('.');
                    track(`${idArr[0]}.ViewAllFilters.${idArr[1]}`, `${checkbox.title}.ViewAllFilters`);
                  },
                }}
                hideScrim
                expandLabel={showListState.seeMoreLabel}
                collapseLabel={showListState.seeLessLabel}
              >
                {checkboxes}
              </UitkExpandoPeek>
            </>
          ) : (
            checkboxes
          )}
        </fieldset>
      </UitkSpacing>
    </>
  );
};

export const Checkboxes = observer(
  ({ getFilterType, getFirstAndLastIndices, handleChange, isChecked, model, modelKey, sortAndFilterOptions }) => {
    const singleColumnNames = ['price', 'travelerType', 'accessibility'];
    const checkbox: FilterModel = sortAndFilterOptions && sortAndFilterOptions[modelKey];
    const checkboxOptions: FilterOptionModel[] = checkbox && checkbox.options;
    const checkboxName: string = checkbox && checkbox.title;
    const isRange = checkbox && checkbox.range;

    const values = (model && model[checkboxName]) || [];

    const { firstIndex = {}, lastIndex = {} } = (isRange && getFirstAndLastIndices(checkboxOptions, values)) || {};

    const getCheckboxes = (columns: UitkLayoutGridColumnRow) => (
      <UitkSpacing padding={{ inline: 'one' }}>
        <UitkLayoutGrid columns={columns}>
          {checkboxOptions.map((option, i) => (
            <UitkLayoutGridItem key={`${checkboxName}-item-${i}`}>
              <CheckboxInput
                key={`${checkboxName}-${i}`}
                index={i}
                checked={typeof isChecked === 'function' ? isChecked(option) : values.includes(option.value)}
                handleChange={handleChange}
                firstIndex={firstIndex}
                lastIndex={lastIndex}
                name={checkboxName}
                ariaLabel={option.ariaLabel}
                label={option.label}
                value={option.value}
                isRange={isRange}
                description={option.description}
                filterType={getFilterType && getFilterType(option)}
              />
            </UitkLayoutGridItem>
          ))}
        </UitkLayoutGrid>
      </UitkSpacing>
    );

    const getCheckboxLayout = () => (
      <Viewport>
        <ViewSmall>{getCheckboxes(singleColumnNames.includes(checkboxName) ? 1 : 2)}</ViewSmall>
        <ViewLarge>{getCheckboxes(1)}</ViewLarge>
      </Viewport>
    );

    return (
      <React.Fragment>
        <Experiment name="PWA_JPSmallCrafts_HoldOut">
          <ExperimentControl>{getCheckboxes(1)}</ExperimentControl>
          <ExperimentVariant bucket={1}>{getCheckboxLayout()}</ExperimentVariant>
          <ExperimentVariant bucket={2}>{getCheckboxLayout()}</ExperimentVariant>
        </Experiment>
      </React.Fragment>
    );
  }
);

export const CheckboxGroup = FormFieldBinding(CheckboxGroupComponent);
